/**
 * One unreleased entry on the Board. Click the narrative to edit it with the
 * case and code menus; hours step in tenths (the server snaps to the firm's
 * rounding); Release sends it to Billing review.
 */
import {
  AlertCircle,
  ArrowRight,
  ChevronDown,
  Scissors,
  Trash2
} from "lucide-react";
import { useEffect, useRef, useState, type FC } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { timeActions } from "../actions";
import { ActivityList } from "../components/ActivityList";
import { CandidateList } from "../components/CandidateList";
import { CaseDot, CasePicker } from "../components/CasePicker";
import { CodeSelect } from "../components/CodeSelect";
import { HoursStepper } from "../components/HoursStepper";
import { releaseBlock } from "../model";
import {
  registerFlush,
  useNarrative,
  useNarrativeDrafts
} from "../narrativeDrafts";
import type { TimeEntry } from "../types";
import { formatDuration, formatMoney } from "../utils";

type Props = {
  entry: TimeEntry;
  open: boolean;
  checked: boolean;
  onOpen: () => void;
  onCheck: () => void;
  onSplit?: (ids: string[]) => void;
};

export const NarrativeEditor: FC<{
  entry: TimeEntry;
  rows?: number;
  locked?: boolean;
  autoFocus?: boolean;
}> = ({ entry, rows = 3, locked, autoFocus }) => {
  const [text, setText] = useState(entry.narrative);
  const ref = useRef<HTMLTextAreaElement>(null);
  const pending = useRef<{ text: string; timer: number } | null>(null);
  const saved = useRef(entry.narrative.trim());
  useEffect(() => {
    saved.current = entry.narrative.trim();
    if (document.activeElement !== ref.current) setText(entry.narrative);
  }, [entry.narrative]);

  const flush = () => {
    const job = pending.current;
    if (!job) return;
    window.clearTimeout(job.timer);
    pending.current = null;
    const next = job.text.trim();
    if (next === saved.current) {
      useNarrativeDrafts.getState().clearDraft(entry.id, job.text);
      return;
    }
    saved.current = next;
    void timeActions
      .update(entry.id, { narrative: next })
      .finally(() =>
        useNarrativeDrafts.getState().clearDraft(entry.id, job.text)
      );
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    const unregister = registerFlush(entry.id, () => flushRef.current());
    return () => {
      unregister();
      flushRef.current();
    };
  }, [entry.id]);

  const change = (value: string) => {
    setText(value);
    useNarrativeDrafts.getState().setDraft(entry.id, value);
    if (pending.current) window.clearTimeout(pending.current.timer);
    pending.current = {
      text: value,
      timer: window.setTimeout(() => flushRef.current(), 800)
    };
  };

  return (
    <Textarea
      ref={ref}
      value={text}
      rows={rows}
      readOnly={locked}
      autoFocus={autoFocus}
      placeholder="Write a narrative for this entry"
      className="resize-y text-base leading-4.75"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => change(e.target.value)}
      onBlur={flush}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
          (e.target as HTMLTextAreaElement).blur();
      }}
      data-testid="narrative-editor"
    />
  );
};

export const FlatFeeTag: FC<{ entry: Pick<TimeEntry, "fee_arrangement"> }> = ({
  entry
}) =>
  entry.fee_arrangement === "flat_fee" ? (
    <span className="shrink-0 text-sm text-fg-3">Flat Fee</span>
  ) : entry.fee_arrangement === "non_billable" ? (
    <span className="shrink-0 text-sm text-fg-3">Non-Billable</span>
  ) : null;

export const BoardCard: FC<Props> = ({
  entry,
  open,
  checked,
  onOpen,
  onCheck,
  onSplit
}) => {
  const needsCase = entry.status === "needs_case" || !entry.individual_id;
  const narrative = useNarrative(entry);
  const block = releaseBlock({ ...entry, narrative });
  const hourly =
    entry.fee_arrangement === "hourly" ||
    entry.fee_arrangement === "hourly_capped";
  return (
    <div
      data-testid="board-card"
      data-entry-id={entry.id}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        "flex flex-col gap-2.5 rounded-xl border bg-surface p-3 transition-colors",
        open ? "border-primary" : "border-line-2"
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        {needsCase ? (
          <AlertCircle className="size-3.5 shrink-0 text-warn" />
        ) : (
          <Checkbox
            checked={checked}
            onCheckedChange={onCheck}
            aria-label="Select entry"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {needsCase ? (
          <CasePicker
            value={null}
            onPick={(c) => void timeActions.assign(entry.id, c.individual_id)}
            trigger={
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded text-base font-medium text-warn hover:underline"
                data-testid="needs-case-pick"
              >
                Needs a case
                <ChevronDown className="size-3.5" />
              </button>
            }
          />
        ) : (
          <span className="flex min-w-0 items-center gap-1.5">
            <CaseDot id={entry.individual_id} />
            <span className="truncate text-base font-medium">
              {entry.case_label}
            </span>
          </span>
        )}
        {entry.status === "returned" ? (
          <Badge className="h-5 bg-warn-soft text-sm text-warn">Returned</Badge>
        ) : null}
        {entry.origin === "assistant" ? (
          <Badge variant="secondary" className="h-5 text-sm font-normal">
            By Casey
          </Badge>
        ) : null}
        <span className="flex-1" />
        {needsCase ? null : <FlatFeeTag entry={entry} />}
      </div>

      {entry.status === "returned" && entry.return_note ? (
        <p className="rounded-md bg-warn-soft px-2 py-1.5 text-sm text-warn">
          Billing: {entry.return_note}
        </p>
      ) : null}

      {needsCase && entry.candidates.length ? (
        <CandidateList
          candidates={entry.candidates}
          onPick={(c) => void timeActions.assign(entry.id, c.individual_id)}
        />
      ) : null}

      {open ? (
        <NarrativeEditor entry={entry} autoFocus />
      ) : (
        <button
          type="button"
          title="Click to edit"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className={cn(
            "cursor-text text-left text-base leading-4.75",
            narrative ? "text-fg" : "text-fg-3"
          )}
        >
          {narrative ||
            (block && !needsCase
              ? "Write a narrative to release this hourly entry"
              : "Write a narrative for this entry")}
        </button>
      )}

      {open ? (
        <div className="flex flex-wrap items-center gap-2">
          <CasePicker
            value={entry.individual_id}
            label={entry.case_label}
            onPick={(c) => void timeActions.assign(entry.id, c.individual_id)}
          />
          <CodeSelect
            value={entry.utbms_activity}
            onChange={(code) =>
              void timeActions.update(entry.id, { utbms_activity: code })
            }
          />
          <span className="flex-1" />
          <Button size="xs" variant="ghost" onClick={onOpen}>
            Done
          </Button>
        </div>
      ) : null}

      <ActivityList rows={entry.activity ?? []} max={open ? 20 : 3} />

      <div className="flex items-center gap-2.5">
        {entry.origin === "captured" && entry.duration_seconds_raw ? (
          <span className="text-sm text-fg-3 tabular-nums">
            {formatDuration(entry.duration_seconds_raw)} captured
          </span>
        ) : null}
        {open ? null : (
          <span className="font-mono text-sm text-fg-3">
            {entry.utbms_activity}
          </span>
        )}
        {hourly && entry.amount ? (
          <span className="text-sm text-fg-3 tabular-nums">
            {formatMoney(entry.amount)}
          </span>
        ) : null}
        <span className="flex-1" />
        {open ? (
          <>
            <Button
              size="xs"
              variant="secondary"
              disabled={entry.duration_hours < 0.2}
              onClick={async () => {
                const out = await timeActions.split(entry);
                if (out) onSplit?.(out.map((e) => e.id));
              }}
            >
              <Scissors /> Split
            </Button>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => void timeActions.discard([entry.id])}
            >
              <Trash2 /> Discard
            </Button>
          </>
        ) : null}
        <HoursStepper
          value={entry.duration_hours}
          onChange={(h) =>
            void timeActions.update(entry.id, { duration_hours: h })
          }
        />
        <Button
          variant="primary"
          size="xs"
          disabled={Boolean(block)}
          title={block ?? undefined}
          onClick={() => void timeActions.release([entry])}
        >
          Release <ArrowRight />
        </Button>
      </div>
    </div>
  );
};
